(function () {
  const panelSelector = '[data-wiw-cart-complete-look]';
  const formSelector = '[data-wiw-look-form]';
  const loadedPanels = new WeakMap();
  const revealTimers = new WeakMap();
  const drawerStates = new WeakMap();
  const drawerObservers = new WeakMap();
  const mobileScrollers = new WeakSet();
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const revealDelay = 1500;

  function getRoutesRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    if (window.routes && window.routes.root_url) {
      return window.routes.root_url;
    }

    return '/';
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    const activeCurrency =
      currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'AUD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function getProductImage(product) {
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.src) return product.featured_image.src;
    }

    if (product.images && product.images.length) {
      const firstImage = product.images[0];
      if (typeof firstImage === 'string') return firstImage;
      if (firstImage.src) return firstImage.src;
    }

    return '';
  }

  function getAvailableVariants(product) {
    return (product.variants || []).filter((variant) => variant.available);
  }

  function getVariantLabel(variant) {
    return variant.public_title || variant.title || 'Default';
  }

  function getOptionName(option) {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.name || '';
  }

  function getOptionPosition(product, names) {
    const options = product.options || [];
    const match = options.findIndex((option) => {
      const optionName = getOptionName(option).toLowerCase();
      return names.some((name) => optionName === name || optionName.includes(name));
    });

    return match >= 0 ? match + 1 : 0;
  }

  function getVariantOptionValue(variant, position) {
    if (!position) return '';
    return variant[`option${position}`] || '';
  }

  function getUniqueValues(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function getVariantImage(variant) {
    const image = variant.featured_image || variant.image;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || '';
  }

  function getSwatchColor(value) {
    const color = String(value || '').toLowerCase();
    const swatches = {
      aqua: '#7fd4e8',
      azure: '#4fa5d8',
      beige: '#d8c7a7',
      black: '#111111',
      blue: '#3f78b5',
      blush: '#f3c6c2',
      brown: '#7a5139',
      coral: '#f47f6b',
      cream: '#f5eedf',
      green: '#5f8f6b',
      grey: '#a7a7a7',
      gray: '#a7a7a7',
      ivory: '#fff7e7',
      khaki: '#b5aa7a',
      lilac: '#c8a7df',
      navy: '#1d2f56',
      natural: '#e8ddc7',
      orange: '#f39a4a',
      pink: '#f1a8bd',
      purple: '#8c5bb3',
      red: '#c9463d',
      sand: '#d8c5a3',
      sky: '#badcf4',
      teal: '#2f9c9c',
      turquoise: '#24adc2',
      white: '#ffffff',
      yellow: '#f4d45d',
    };

    const swatchKey = Object.keys(swatches).find((key) => color.includes(key));
    return swatchKey ? swatches[swatchKey] : '#f4f4f4';
  }

  function prepareVariantData(variants) {
    return variants.map((variant) => ({
      id: variant.id,
      title: variant.title,
      public_title: variant.public_title,
      price: variant.price,
      option1: variant.option1 || '',
      option2: variant.option2 || '',
      option3: variant.option3 || '',
      image: getVariantImage(variant),
    }));
  }

  function renderVariantField(product, variants, currency) {
    if (variants.length === 1) {
      return `<input type="hidden" name="id" value="${variants[0].id}">`;
    }

    const colorPosition = getOptionPosition(product, ['color', 'colour']);
    const sizePosition = getOptionPosition(product, ['size']);
    const colors = colorPosition
      ? getUniqueValues(variants.map((variant) => getVariantOptionValue(variant, colorPosition)))
      : [];
    const initialColor = colors[0] || '';
    const matchingInitialVariants = initialColor
      ? variants.filter((variant) => getVariantOptionValue(variant, colorPosition) === initialColor)
      : variants;
    const initialVariant = matchingInitialVariants[0] || variants[0];
    const variantData = escapeHtml(JSON.stringify(prepareVariantData(variants)));

    if (colorPosition && colors.length > 1) {
      const swatches = colors
        .map((color, index) => {
          const isActive = index === 0;
          return `
            <button
              class="wiw-look-card__swatch${isActive ? ' is-active' : ''}"
              type="button"
              style="--swatch-color: ${getSwatchColor(color)}"
              data-wiw-color-swatch
              data-color="${escapeHtml(color)}"
              aria-label="Choose ${escapeHtml(color)}"
              aria-pressed="${isActive ? 'true' : 'false'}"
            >
              <span class="wiw-look-card__swatch-name">${escapeHtml(color)}</span>
            </button>
          `;
        })
        .join('');

      const sizeOptions = matchingInitialVariants
        .map((variant) => {
          const label = sizePosition ? getVariantOptionValue(variant, sizePosition) : getVariantLabel(variant);
          return `<option value="${variant.id}">${escapeHtml(label)}</option>`;
        })
        .join('');

      return `
        <input type="hidden" name="id" value="${initialVariant.id}" data-wiw-variant-input>
        <div
          class="wiw-look-card__variant-picker"
          data-wiw-variants="${variantData}"
          data-color-position="${colorPosition}"
          data-size-position="${sizePosition}"
          data-currency="${escapeHtml(currency || '')}"
        >
          <div>
            <span class="wiw-look-card__option-label">Colour</span>
            <div class="wiw-look-card__swatches">${swatches}</div>
          </div>
          <div>
            <label class="wiw-look-card__option-label" for="wiw-look-variant-${product.id}">Size</label>
            <select class="wiw-look-card__select" id="wiw-look-variant-${product.id}" data-wiw-size-select>
              ${sizeOptions}
            </select>
          </div>
        </div>
      `;
    }

    const options = variants
      .map(
        (variant) =>
          `<option value="${variant.id}">${escapeHtml(getVariantLabel(variant))}</option>`
      )
      .join('');

    return `
      <label class="visually-hidden" for="wiw-look-variant-${product.id}">Choose option</label>
      <select class="wiw-look-card__select" id="wiw-look-variant-${product.id}" name="id">
        ${options}
      </select>
    `;
  }

  function renderProductCard(product, currency) {
    const variants = getAvailableVariants(product);
    const image = getProductImage(product);
    const imageMarkup = image
      ? `<img class="wiw-look-card__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
      : `<span class="wiw-look-card__placeholder">West Indies Wear</span>`;

    return `
      <article class="wiw-look-card" data-product-id="${product.id}">
        <a class="wiw-look-card__image-wrap" href="${escapeHtml(product.url || '#')}" tabindex="-1">
          ${imageMarkup}
        </a>
        <div class="wiw-look-card__content">
          <a class="wiw-look-card__title" href="${escapeHtml(product.url || '#')}">
            ${escapeHtml(product.title)}
          </a>
          <p class="wiw-look-card__price">${formatMoney(product.price_min || product.price, currency)}</p>
          <form class="wiw-look-card__form" data-wiw-look-form>
            ${renderVariantField(product, variants, currency)}
            <button class="wiw-look-card__button" type="submit">
              Add
            </button>
          </form>
        </div>
      </article>
    `;
  }

  function updateMobileScrollCue(panel) {
    const scroller = panel.querySelector('.wiw-cart-complete-look__grid');
    const button = panel.querySelector('[data-wiw-look-scroll-next]');
    if (!scroller || !button) return;

    const remaining = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
    button.hidden = !scroller.clientWidth || remaining <= 4;
  }

  function setupMobileScrollCue(panel) {
    if (!panel.classList.contains('wiw-cart-complete-look--mobile')) return;

    const scroller = panel.querySelector('.wiw-cart-complete-look__grid');
    if (!scroller) return;

    if (!mobileScrollers.has(scroller)) {
      scroller.addEventListener('scroll', () => updateMobileScrollCue(panel), { passive: true });
      mobileScrollers.add(scroller);
    }

    window.requestAnimationFrame(() => updateMobileScrollCue(panel));
  }

  function handleMobileScrollNext(event) {
    const button = event.target.closest('[data-wiw-look-scroll-next]');
    if (!button) return;

    const panel = button.closest('.wiw-cart-complete-look--mobile');
    const scroller = panel && panel.querySelector('.wiw-cart-complete-look__grid');
    if (!scroller) return;

    scroller.scrollBy({
      left: Math.max(scroller.clientWidth * 0.72, 150),
      behavior: 'smooth',
    });
  }

  function renderEmpty(panel, message) {
    const list = panel.querySelector('[data-wiw-complete-look-list]');
    const count = panel.querySelector('[data-wiw-complete-look-count]');

    if (count) count.textContent = '';
    if (list) {
      list.innerHTML = `<p class="wiw-cart-complete-look__empty">${escapeHtml(message)}</p>`;
    }
  }

  async function fetchRecommendations(productId, limit, intent) {
    const url = new URL(`${getRoutesRoot()}recommendations/products.json`, window.location.origin);

    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', limit);
    url.searchParams.set('intent', intent);

    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error(`${intent} recommendation request failed`);

    const data = await response.json();

    return data.products || [];
  }

  function getUsableProducts(products, cartProductIds, limit) {
    return products
      .filter((product) => !cartProductIds.has(String(product.id)))
      .filter((product) => product.available)
      .filter((product) => getAvailableVariants(product).length > 0)
      .slice(0, Number(limit));
  }

  function clearRevealTimer(panel) {
    if (!revealTimers.has(panel)) return;

    window.clearTimeout(revealTimers.get(panel));
    revealTimers.delete(panel);
  }

  function getDrawerState(drawer) {
    if (!drawerStates.has(drawer)) {
      const isActive = drawer.classList.contains('active');
      drawerStates.set(drawer, {
        isActive,
        openedAt: isActive ? performance.now() : 0,
        manuallyToggled: false,
      });
    }

    return drawerStates.get(drawer);
  }

  function setDesktopPanelState(panel, isOpen, isWaiting) {
    const drawer = panel.closest('cart-drawer');
    const layout = panel.closest('.wiw-cart-drawer__layout');
    if (!layout) return;

    layout.classList.toggle('wiw-cart-drawer__layout--recommendations-open', isOpen);
    layout.classList.toggle('wiw-cart-drawer__layout--recommendations-collapsed', !isOpen);
    layout.classList.toggle(
      'wiw-cart-drawer__layout--recommendations-auto-waiting',
      !isOpen && Boolean(isWaiting)
    );

    if (drawer) {
      drawer.classList.toggle('wiw-cart-drawer--recommendations-open', isOpen);
      drawer.classList.toggle('wiw-cart-drawer--recommendations-collapsed', !isOpen);
    }

    updateToggle(panel, isOpen);
  }

  function scheduleDesktopReveal(panel) {
    const drawer = panel.closest('cart-drawer');
    if (!drawer || panel.hidden || !desktopQuery.matches) return;

    const state = getDrawerState(drawer);
    if (!drawer.classList.contains('active') || state.manuallyToggled) return;

    clearRevealTimer(panel);
    setDesktopPanelState(panel, false, true);

    const elapsed = state.openedAt ? performance.now() - state.openedAt : 0;
    const delay = Math.max(0, revealDelay - elapsed);
    const timer = window.setTimeout(() => {
      const currentState = getDrawerState(drawer);

      if (
        !panel.isConnected ||
        panel.hidden ||
        !desktopQuery.matches ||
        !drawer.classList.contains('active') ||
        currentState.manuallyToggled
      ) {
        revealTimers.delete(panel);
        return;
      }

      setDesktopPanelState(panel, true, false);
      revealTimers.delete(panel);
    }, delay);

    revealTimers.set(panel, timer);
  }

  function handleDrawerActivity(drawer) {
    const state = getDrawerState(drawer);
    const isActive = drawer.classList.contains('active');
    if (state.isActive === isActive) return;

    state.isActive = isActive;
    state.manuallyToggled = false;
    state.openedAt = isActive ? performance.now() : 0;

    const panel = drawer.querySelector('.wiw-cart-complete-look--desktop');
    if (!panel || panel.hidden) return;

    clearRevealTimer(panel);

    if (isActive) {
      scheduleDesktopReveal(panel);
    } else {
      setDesktopPanelState(panel, false, false);
    }
  }

  function watchDrawer(drawer) {
    if (!drawer || drawerObservers.has(drawer)) return;

    getDrawerState(drawer);

    const observer = new MutationObserver(() => handleDrawerActivity(drawer));
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
    drawerObservers.set(drawer, observer);
  }

  function setPanelVisibility(panel, isVisible) {
    const drawer = panel.closest('cart-drawer');
    const layout = panel.closest('.wiw-cart-drawer__layout');
    const isDesktopPanel = panel.classList.contains('wiw-cart-complete-look--desktop');

    clearRevealTimer(panel);

    panel.hidden = !isVisible;

    if (layout) {
      const hasDesktopPanel = Boolean(
        layout.querySelector('.wiw-cart-complete-look--desktop:not([hidden])')
      );
      layout.classList.toggle('wiw-cart-drawer__layout--recommendations-ready', hasDesktopPanel);

      if (!hasDesktopPanel) {
        layout.classList.remove(
          'wiw-cart-drawer__layout--recommendations-open',
          'wiw-cart-drawer__layout--recommendations-collapsed',
          'wiw-cart-drawer__layout--recommendations-auto-waiting'
        );
      }
    }

    if (drawer) {
      const hasDesktopPanel = Boolean(
        drawer.querySelector('.wiw-cart-complete-look--desktop:not([hidden])')
      );
      drawer.classList.toggle('wiw-cart-drawer--recommendations-ready', hasDesktopPanel);

      if (!hasDesktopPanel) {
        drawer.classList.remove(
          'wiw-cart-drawer--recommendations-open',
          'wiw-cart-drawer--recommendations-collapsed'
        );
      }
    }

    if (!isDesktopPanel || !layout) return;

    setDesktopPanelState(panel, false, false);

    if (isVisible && drawer) {
      watchDrawer(drawer);
      scheduleDesktopReveal(panel);
    }
  }

  function updateToggle(panel, isOpen) {
    const layout = panel.closest('.wiw-cart-drawer__layout');
    const toggles = layout
      ? layout.querySelectorAll('[data-wiw-complete-look-toggle]')
      : panel.querySelectorAll('[data-wiw-complete-look-toggle]');

    toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.setAttribute(
        'aria-label',
        isOpen ? 'Hide complete the look recommendations' : 'Show complete the look recommendations'
      );

      const icon = toggle.querySelector('.wiw-cart-complete-look__toggle-icon');
      if (icon) icon.textContent = isOpen ? '‹' : '›';
    });
  }

  function handleToggleClick(event) {
    const toggle = event.target.closest('[data-wiw-complete-look-toggle]');
    if (!toggle) return;

    const layout = toggle.closest('.wiw-cart-drawer__layout');
    const panel =
      toggle.closest('[data-wiw-cart-complete-look]') ||
      (layout && layout.querySelector('.wiw-cart-complete-look--desktop'));
    if (!panel || !layout) return;

    clearRevealTimer(panel);

    const willOpen = !layout.classList.contains('wiw-cart-drawer__layout--recommendations-open');
    const drawer = panel.closest('cart-drawer');
    if (drawer) {
      getDrawerState(drawer).manuallyToggled = true;
    }
    setDesktopPanelState(panel, willOpen, false);
  }

  function getPickerVariants(picker) {
    try {
      return JSON.parse(picker.dataset.wiwVariants || '[]');
    } catch (error) {
      return [];
    }
  }

  function getPickerOptionValue(variant, position) {
    return position ? variant[`option${position}`] || '' : '';
  }

  function updateCardImage(form, variant) {
    if (!variant || !variant.image) return;

    const card = form.closest('.wiw-look-card');
    const image = card && card.querySelector('.wiw-look-card__image');
    if (image) image.src = variant.image;
  }

  function setActiveVariant(form, variant) {
    const input = form.querySelector('[data-wiw-variant-input]');
    if (input && variant) input.value = variant.id;
    updateCardImage(form, variant);
  }

  function updateSizesForColor(form, color) {
    const picker = form.querySelector('[data-wiw-variants]');
    const select = form.querySelector('[data-wiw-size-select]');
    if (!picker) return;

    const variants = getPickerVariants(picker);
    const colorPosition = Number(picker.dataset.colorPosition || 0);
    const sizePosition = Number(picker.dataset.sizePosition || 0);
    const matchingVariants = variants.filter(
      (variant) => getPickerOptionValue(variant, colorPosition) === color
    );

    if (select) {
      select.innerHTML = matchingVariants
        .map((variant) => {
          const label = sizePosition
            ? getPickerOptionValue(variant, sizePosition)
            : variant.public_title || variant.title || 'Default';
          return `<option value="${variant.id}">${escapeHtml(label)}</option>`;
        })
        .join('');
    }

    setActiveVariant(form, matchingVariants[0]);
  }

  function handleColorSwatchClick(event) {
    const swatch = event.target.closest('[data-wiw-color-swatch]');
    if (!swatch) return;

    const form = swatch.closest(formSelector);
    if (!form) return;

    form.querySelectorAll('[data-wiw-color-swatch]').forEach((button) => {
      const isActive = button === swatch;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    updateSizesForColor(form, swatch.dataset.color || '');
  }

  function handleSizeChange(event) {
    const select = event.target.closest('[data-wiw-size-select]');
    if (!select) return;

    const form = select.closest(formSelector);
    const picker = form && form.querySelector('[data-wiw-variants]');
    if (!form || !picker) return;

    const variants = getPickerVariants(picker);
    const selectedVariant = variants.find((variant) => String(variant.id) === String(select.value));
    setActiveVariant(form, selectedVariant);
  }

  async function loadPanel(panel) {
    const productId = panel.dataset.productId;
    const cartProductIds = new Set(
      (panel.dataset.cartProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
    const currency = panel.dataset.currency;
    const limit = panel.dataset.limit || '8';
    const loadKey = `${productId}:${Array.from(cartProductIds).join(',')}`;

    if (!productId || loadedPanels.get(panel) === loadKey) return;

    loadedPanels.set(panel, loadKey);

    const list = panel.querySelector('[data-wiw-complete-look-list]');
    const count = panel.querySelector('[data-wiw-complete-look-count]');

    try {
      const complementaryProducts = await fetchRecommendations(productId, limit, 'complementary');
      let products = getUsableProducts(complementaryProducts, cartProductIds, limit);

      if (!products.length) {
        const relatedProducts = await fetchRecommendations(productId, limit, 'related');
        products = getUsableProducts(relatedProducts, cartProductIds, limit);
      }

      if (!products.length) {
        setPanelVisibility(panel, false);
        renderEmpty(panel, 'No outfit suggestions are available yet.');
        return;
      }

      if (count) count.textContent = `${products.length}`;
      if (list) {
        list.innerHTML = `
          <div class="wiw-cart-complete-look__grid">${products
            .map((product) => renderProductCard(product, currency))
            .join('')}</div>
          <button
            class="wiw-cart-complete-look__scroll-next"
            type="button"
            aria-label="Show more outfit suggestions"
            data-wiw-look-scroll-next
            hidden
          >
            <span aria-hidden="true">›</span>
          </button>
        `;
      }

      setPanelVisibility(panel, true);
      setupMobileScrollCue(panel);
    } catch (error) {
      setPanelVisibility(panel, false);
      renderEmpty(panel, 'Outfit suggestions could not load right now.');
      console.error(error);
    }
  }

  function initPanels() {
    document.querySelectorAll('cart-drawer').forEach((drawer) => watchDrawer(drawer));
    document.querySelectorAll(panelSelector).forEach((panel) => loadPanel(panel));
  }

  function handleViewportChange() {
    document.querySelectorAll('.wiw-cart-complete-look--desktop:not([hidden])').forEach((panel) => {
      clearRevealTimer(panel);
      setDesktopPanelState(panel, false, false);
      scheduleDesktopReveal(panel);
    });
    document.querySelectorAll('.wiw-cart-complete-look--mobile:not([hidden])').forEach((panel) => {
      setupMobileScrollCue(panel);
    });
  }

  async function handleRecommendationSubmit(event) {
    const form = event.target.closest(formSelector);
    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const formData = new FormData(form);
    const variantId = formData.get('id');

    if (!variantId || !button) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding';

    try {
      const cartAddUrl =
        window.routes && window.routes.cart_add_url
          ? window.routes.cart_add_url
          : `${getRoutesRoot()}cart/add.js`;
      const cartUrl =
        window.routes && window.routes.cart_url ? window.routes.cart_url : `${getRoutesRoot()}cart`;

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Add to cart failed');
      }

      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState);
        window.setTimeout(initPanels, 150);
      } else {
        window.location.href = cartUrl;
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try again';
      console.error(error);

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }
  }

  document.addEventListener('submit', handleRecommendationSubmit);
  document.addEventListener('click', handleColorSwatchClick);
  document.addEventListener('click', handleToggleClick);
  document.addEventListener('click', handleMobileScrollNext);
  document.addEventListener('change', handleSizeChange);
  document.addEventListener('DOMContentLoaded', initPanels);
  desktopQuery.addEventListener('change', handleViewportChange);
  window.addEventListener('resize', () => {
    document.querySelectorAll('.wiw-cart-complete-look--mobile:not([hidden])').forEach((panel) => {
      setupMobileScrollCue(panel);
    });
  });

  const observer = new MutationObserver(() => {
    window.clearTimeout(observer.timeout);
    observer.timeout = window.setTimeout(initPanels, 80);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
